import originalAxios from "axios";
import { config } from "../config";

/**
 * Extend Axios request config to allow individual requests to opt out of the
 * global 401 redirect. Used by getCurrentUser (session-check on load) so that
 * an unauthenticated `GET /me` response is handled at the query level instead
 * of triggering a hard redirect before React state has settled.
 */
declare module "axios" {
    interface AxiosRequestConfig {
        skipAuthRedirect?: boolean;
    }
}

/**
 * Shared Axios instance for Smart Mail API.
 * Uses session-based auth (Sanctum SPA flow):
 *   - withCredentials: true → sends session cookie on every request
 *   - withXSRFToken: true → axios 1.x explicit opt-in to read the XSRF-TOKEN
 *     cookie and set the X-XSRF-TOKEN header on mutating requests. Required
 *     because axios's automatic same-origin detection can be fragile with a
 *     relative baseURL like "/api/v1" + Vite dev server proxying.
 *   - 401 responses trigger a full redirect to the login page (unless
 *     skipAuthRedirect is set on the individual request config)
 */
export const axios = originalAxios.create({
    baseURL: config.apiUrl,
    withCredentials: true,
    withXSRFToken: true,
    headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
    },
});

axios.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error?.response?.status === 401 && !error.config?.skipAuthRedirect) {
            window.location.href = "/auth/sign-in";
        }
        return Promise.reject(error);
    },
);
